import { API_BASE } from '../lib/config';
/**
 * ModulesContext - Module System Provider
 *
 * Effective state is resolved for the active team when one exists.
 * Organization settings are the upper-level ceiling; teams may disable
 * optional modules further through team-level overrides.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = API_BASE;

export const MODULE_KEYS = {
  TRAINING: 'training',
  MATCH: 'match',
  CALENDAR: 'calendar',
  SQUAD: 'squad',
  PLAYER_DEVELOPMENT: 'player_development',
  CHAT: 'chat',
  TACTIC: 'tactic',
  EVALUATIONS: 'evaluations',
  RATINGS: 'ratings',
  INDIVIDUAL_TRAINING: 'individual_training',
  TESTS: 'tests',
  STATISTICS: 'statistics',
  CHALLENGES: 'challenges',
  PRAISE: 'praise',
  MEDIA: 'media',
  POSTS: 'posts',
};

const ALL_ENABLED = {
  [MODULE_KEYS.TRAINING]: true,
  [MODULE_KEYS.MATCH]: true,
  [MODULE_KEYS.CALENDAR]: true,
  [MODULE_KEYS.SQUAD]: true,
  [MODULE_KEYS.PLAYER_DEVELOPMENT]: true,
  [MODULE_KEYS.CHAT]: true,
  [MODULE_KEYS.TACTIC]: true,
  [MODULE_KEYS.EVALUATIONS]: true,
  [MODULE_KEYS.RATINGS]: true,
  [MODULE_KEYS.INDIVIDUAL_TRAINING]: true,
  [MODULE_KEYS.TESTS]: true,
  [MODULE_KEYS.STATISTICS]: true,
  [MODULE_KEYS.CHALLENGES]: true,
  [MODULE_KEYS.PRAISE]: true,
  [MODULE_KEYS.MEDIA]: true,
  [MODULE_KEYS.POSTS]: true,
};

// IMPORTANT: specific /development routes must come before the broad hub route.
const ROUTE_MODULE_MAP = {
  '/development/evaluations': MODULE_KEYS.EVALUATIONS,
  '/development/assessments': MODULE_KEYS.RATINGS,
  '/development/tests': MODULE_KEYS.TESTS,
  '/utveckling/individuell-traning': MODULE_KEYS.INDIVIDUAL_TRAINING,
  '/min-traning': MODULE_KEYS.INDIVIDUAL_TRAINING,
  '/development/statistics': MODULE_KEYS.STATISTICS,
  '/development/praise': MODULE_KEYS.PRAISE,
  '/development/challenges': MODULE_KEYS.CHALLENGES,
  '/team/development': MODULE_KEYS.PLAYER_DEVELOPMENT,
  '/development/conversations': MODULE_KEYS.PLAYER_DEVELOPMENT,
  '/development': MODULE_KEYS.PLAYER_DEVELOPMENT,
  '/chat': MODULE_KEYS.CHAT,
  '/training': MODULE_KEYS.TRAINING,
  '/matches': MODULE_KEYS.MATCH,
  '/calendar': MODULE_KEYS.CALENDAR,
  '/evaluations': MODULE_KEYS.EVALUATIONS,
  '/assessments': MODULE_KEYS.RATINGS,
  '/statistics': MODULE_KEYS.STATISTICS,
  '/media': MODULE_KEYS.MEDIA,
  '/posts': MODULE_KEYS.POSTS,
  '/tactics': MODULE_KEYS.TACTIC,
  '/praise': MODULE_KEYS.PRAISE,
  '/challenges': MODULE_KEYS.CHALLENGES,
};

// Some routes share a prefix with system/admin pages. Trupp therefore guards
// only the exact /team page; /team/settings must always remain reachable so an
// admin can turn the module back on.
const EXACT_ROUTE_MODULE_MAP = {
  '/team': MODULE_KEYS.SQUAD,
};

const QUICK_ACTION_MODULE_MAP = {
  'Ny träning': MODULE_KEYS.TRAINING,
  'Ny match': MODULE_KEYS.MATCH,
  'Ge beröm': MODULE_KEYS.PRAISE,
  'Utvärderingar': MODULE_KEYS.EVALUATIONS,
  'Bedömningar': MODULE_KEYS.RATINGS,
  'Individuell träning': MODULE_KEYS.INDIVIDUAL_TRAINING,
  'Tester': MODULE_KEYS.TESTS,
  'Statistik': MODULE_KEYS.STATISTICS,
  'Kalender': MODULE_KEYS.CALENDAR,
  'Taktik': MODULE_KEYS.TACTIC,
  'Chatt': MODULE_KEYS.CHAT,
  'Trupp': MODULE_KEYS.SQUAD,
  'Spelarutveckling': MODULE_KEYS.PLAYER_DEVELOPMENT,
};

const ModulesContext = createContext(null);

export const useModules = () => {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within a ModulesProvider');
  }
  return context;
};

export const ModulesProvider = ({ children }) => {
  const { activeTeam } = useAuth();
  const [modules, setModules] = useState(ALL_ENABLED);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [canManageModules, setCanManageModules] = useState(false);

  const fetchModules = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setModules(ALL_ENABLED);
      setOrganizationId(null);
      setTeamId(null);
      setCanManageModules(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const query = activeTeam?.id
        ? `?team_id=${encodeURIComponent(activeTeam.id)}`
        : '';
      const res = await fetch(`${API_URL}/api/modules/me${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error(`Module fetch failed with ${res.status}`);
      }

      const data = await res.json();
      setModules({ ...ALL_ENABLED, ...(data.data || {}) });
      setOrganizationId(data.organization_id || null);
      setTeamId(data.team_id || activeTeam?.id || null);
      setCanManageModules(Boolean(data.can_edit));
    } catch (error) {
      console.error('Error fetching modules:', error);
      // Keep navigation usable if the module endpoint is temporarily unavailable.
      // Backend feature gates remain the security source of truth.
      setModules(ALL_ENABLED);
      setOrganizationId(null);
      setTeamId(activeTeam?.id || null);
      setCanManageModules(false);
    } finally {
      setLoading(false);
    }
  }, [activeTeam?.id]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const isModuleEnabled = useCallback((moduleKey) => {
    return modules[moduleKey] !== false;
  }, [modules]);

  const isRouteEnabled = useCallback((path) => {
    // The match workspace also hosts tactics; detail routes retain the match gate.
    if (path === '/matches') return isModuleEnabled(MODULE_KEYS.MATCH) || isModuleEnabled(MODULE_KEYS.TACTIC);
    const exactModuleKey = EXACT_ROUTE_MODULE_MAP[path];
    if (exactModuleKey) return isModuleEnabled(exactModuleKey);

    for (const [routePrefix, moduleKey] of Object.entries(ROUTE_MODULE_MAP)) {
      if (path === routePrefix || path.startsWith(routePrefix + '/')) {
        return isModuleEnabled(moduleKey);
      }
    }
    return true;
  }, [isModuleEnabled]);

  const isQuickActionEnabled = useCallback((actionLabel) => {
    const moduleKey = QUICK_ACTION_MODULE_MAP[actionLabel];
    if (!moduleKey) return true;
    return isModuleEnabled(moduleKey);
  }, [isModuleEnabled]);

  const filterMenuItems = useCallback((items) => {
    return items.filter(item => {
      if (item.moduleKey) return isModuleEnabled(item.moduleKey);
      if (!item.to) return true;
      return isRouteEnabled(item.to);
    });
  }, [isModuleEnabled, isRouteEnabled]);

  const getModuleForRoute = useCallback((path) => {
    if (path === '/matches' && !isModuleEnabled(MODULE_KEYS.MATCH) && isModuleEnabled(MODULE_KEYS.TACTIC)) return MODULE_KEYS.TACTIC;
    const exactModuleKey = EXACT_ROUTE_MODULE_MAP[path];
    if (exactModuleKey) return exactModuleKey;

    for (const [routePrefix, moduleKey] of Object.entries(ROUTE_MODULE_MAP)) {
      if (path === routePrefix || path.startsWith(routePrefix + '/')) {
        return moduleKey;
      }
    }
    return null;
  }, [isModuleEnabled]);

  const value = {
    modules,
    loading,
    organizationId,
    teamId,
    canManageModules,
    isModuleEnabled,
    isRouteEnabled,
    isQuickActionEnabled,
    filterMenuItems,
    getModuleForRoute,
    refetchModules: fetchModules,
    MODULE_KEYS,
  };

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
};

export default ModulesContext;
